/**
 * OuiStuPaStu i18n — EN / FR / DE
 * Usage:
 *   import { useT, LANGS } from '../lib/i18n';
 *   const { t, lang } = useT();
 *   t('quiz.start')
 */
import { useAuth } from '../context/AuthContext';

export const LANGS = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'fr', label: 'French',  native: 'Français' },
  { code: 'de', label: 'German',  native: 'Deutsch' },
];

const T = {
  en: {
    // Landing / brand
    'brand.tagline1': '3 questions a day.',
    'brand.tagline2': 'A lifetime of curiosity.',
    'landing.hero.desc': 'Join Ouistiti, Stupidopoulos, Stupidopoulou and Patapouf on a daily adventure through science, geography, history, language and more — built for kids, parents and teachers.',
    'landing.family_learns': 'A family that learns together',
    'landing.demo_note': 'Demo accounts: parent@test.com · child@test.com · teacher@test.com · password: test123',
    'feature.daily_title': 'Bite-Sized Daily Quiz',
    'feature.daily_desc': 'Learn a little every day with instant feedback and level-ups.',
    'feature.levels_title': '7 Level Ranks',
    'feature.levels_desc': 'From Explorer to Knowble ⭐ — every answer moves you forward.',
    'feature.roles_title': 'Three Roles, One App',
    'feature.roles_desc': 'Kids play, parents track, teachers coach — all in one place.',
    'footer.tag': 'Learn boldly, together.',

    // CTAs
    'cta.start_free': 'Start Learning Free',
    'cta.have_account': 'I have an account',
    'cta.log_in': 'Log in',
    'cta.join_free': 'Join Free',
    'cta.logout': 'Log out',
    'cta.back_home': 'Back to Dashboard',

    // Auth
    'auth.welcome_back': 'Welcome back',
    'auth.login_desc': 'Log in to continue your learning journey.',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.login_btn': 'Log in',
    'auth.signing_in': 'Signing in…',
    'auth.quick_demo': 'Quick demo access',
    'auth.new_here': 'New here?',
    'auth.create_account': 'Create an account',
    'auth.register_title': 'Create your account',
    'auth.register_desc': 'Choose your role to get started.',
    'auth.role_child': 'Child',
    'auth.role_parent': 'Parent',
    'auth.role_teacher': 'Teacher',
    'auth.role_child_desc': 'Play, learn & level up',
    'auth.role_parent_desc': 'Track & subscribe',
    'auth.role_teacher_desc': 'Coach the class',
    'auth.pick_character': 'Pick your character',
    'auth.name': 'Full name',
    'auth.country': 'Country',
    'auth.grade': 'Grade level',
    'auth.language': 'Language',
    'auth.parent_email': "Parent's email (optional)",
    'auth.create_btn': 'Create Account',
    'auth.creating': 'Creating…',
    'auth.already': 'Already have an account?',

    // Grades
    'grade.adult': 'Adult',
    'grade.prefix': 'Grade',

    // Header
    'header.dashboard_child': 'Child Dashboard',
    'header.dashboard_parent': 'Parent Dashboard',
    'header.dashboard_teacher': 'Teacher Dashboard',
    'header.language': 'Language',

    // Levels
    'level.explorer': 'Explorer',
    'level.adventurer': 'Adventurer',
    'level.scholar': 'Scholar',
    'level.sage': 'Sage',
    'level.luminary': 'Luminary',
    'level.master': 'Master',
    'level.knowble': 'Knowble',
    'level.pts': 'pts',
    'level.next_hint': 'points to next level',
    'level.max_hint': 'You reached the top rank! ⭐',

    // Quiz / dashboard
    'quiz.section_kicker': 'Quiz Time',
    'quiz.headline': 'Questions. Big rewards.',
    'quiz.subline': 'Answer as many as you like — every answer moves you closer to Knowble ⭐',
    'quiz.start': 'Start Quiz →',
    'quiz.keep_going': 'Keep going!',
    'quiz.great_job': 'Great job! Ready for more?',
    'quiz.session_answered': "You've answered {n} question(s) this session.",
    'quiz.session_hint': 'Every answer counts toward reaching Knowble ⭐',
    'quiz.next': 'Next Question →',
    'quiz.submit': 'Submit Answer',
    'quiz.checking': 'Checking…',
    'quiz.loading_more': 'Loading…',
    'quiz.finished_btn': 'Back to Dashboard',
    'quiz.correct': "Nice! That's right.",
    'quiz.wrong_prefix': 'The correct answer is',
    'quiz.answered': 'Answered',
    'quiz.pts_earned': 'points earned',
    'quiz.total_pts': 'Total',
    'quiz.no_questions': 'No questions available.',

    // Categories (subject labels)
    'subject.math': 'Math',
    'subject.science': 'Science',
    'subject.geography': 'Geography',
    'subject.history': 'History',
    'subject.language': 'Language',
    'subject.social_studies': 'Social Studies',
    'subject.nature': 'Nature',
    'subject.culture': 'Culture',
    'subject.general': 'General',

    // Habits
    'habits.today': "Today's Habits",
    'habits.subtitle': 'Small wins, every day.',
    'habits.nice_job': 'Nice job!',
    'habits.error': 'Could not save habit',

    // Parent
    'parent.welcome': 'Hi',
    'parent.subtitle': "Track your family's learning and celebrate the wins.",
    'parent.subscription_kicker': 'Family Plan',
    'parent.plan_price': '/ month',
    'parent.plan_desc': 'Unlimited access for every family member, expanded curriculum, and progress reports.',
    'parent.active': 'Active ✓',
    'parent.subscribe': 'Subscribe',
    'parent.subscribe_loading': 'Loading…',
    'parent.your_children': 'Your children',
    'parent.no_children': 'No children linked yet.',
    'parent.link_hint': 'Ask them to register with your email to link accounts.',
    'parent.wins': "This Week's Family Wins",
    'parent.badge_streak_kicker': 'Parent of a Learner',
    'parent.badges_earned': 'badges earned',
    'parent.badge_hint': 'One badge for every week your child hits at least Bronze (5+ questions).',
    'parent.warming_up': 'Warming Up',
    'parent.progress_to': 'Progress to',
    'parent.share': 'Share Achievement',
    'parent.week_headline': 'had a {tier} week!',
    'parent.tier_bronze': 'bronze',
    'parent.tier_silver': 'silver',
    'parent.tier_gold': 'gold',
    'parent.tier_perfect': 'perfect',
    'parent.tier_default': 'great',
    'parent.qh_summary': '{level} Level · {q} question(s) answered · {h} habit(s) completed',

    // Teacher
    'teacher.kicker': 'Classroom',
    'teacher.welcome': 'Welcome,',
    'teacher.subtitle': "Follow your students' daily progress.",
    'teacher.students': 'Students',
    'teacher.avg_points': 'Avg. Points',
    'teacher.total_pts': 'Total Pts',
    'teacher.progress': 'Student progress',
    'teacher.no_students': 'No students yet.',
  },

  fr: {
    'brand.tagline1': '3 questions par jour.',
    'brand.tagline2': "Une vie de curiosité.",
    'landing.hero.desc': "Rejoins Ouistiti, Stupidopoulos, Stupidopoulou et Patapouf dans une aventure quotidienne à travers les sciences, la géographie, l'histoire, la langue et plus — pensée pour les enfants, les parents et les enseignants.",
    'landing.family_learns': 'Une famille qui apprend ensemble',
    'landing.demo_note': "Comptes de démo : parent@test.com · child@test.com · teacher@test.com · mot de passe : test123",
    'feature.daily_title': 'Quiz quotidien court',
    'feature.daily_desc': "Apprends un peu chaque jour avec un retour immédiat et des montées de niveau.",
    'feature.levels_title': '7 niveaux',
    'feature.levels_desc': "De Explorateur à Knowble ⭐ — chaque réponse te fait avancer.",
    'feature.roles_title': 'Trois rôles, une seule appli',
    'feature.roles_desc': "Les enfants jouent, les parents suivent, les enseignants encadrent.",
    'footer.tag': 'Apprendre avec audace, ensemble.',

    'cta.start_free': 'Commencer gratuitement',
    'cta.have_account': "J'ai déjà un compte",
    'cta.log_in': 'Connexion',
    'cta.join_free': 'Rejoindre',
    'cta.logout': 'Déconnexion',
    'cta.back_home': 'Retour au tableau',

    'auth.welcome_back': 'Bon retour',
    'auth.login_desc': "Connecte-toi pour continuer ton parcours d'apprentissage.",
    'auth.email': 'E-mail',
    'auth.password': 'Mot de passe',
    'auth.login_btn': 'Connexion',
    'auth.signing_in': 'Connexion…',
    'auth.quick_demo': 'Accès démo rapide',
    'auth.new_here': 'Nouveau ?',
    'auth.create_account': 'Créer un compte',
    'auth.register_title': 'Créer ton compte',
    'auth.register_desc': 'Choisis ton rôle pour commencer.',
    'auth.role_child': 'Enfant',
    'auth.role_parent': 'Parent',
    'auth.role_teacher': 'Enseignant',
    'auth.role_child_desc': 'Joue, apprends & monte de niveau',
    'auth.role_parent_desc': "Suivi & abonnement",
    'auth.role_teacher_desc': "Anime ta classe",
    'auth.pick_character': 'Choisis ton personnage',
    'auth.name': 'Nom complet',
    'auth.country': 'Pays',
    'auth.grade': 'Niveau scolaire',
    'auth.language': 'Langue',
    'auth.parent_email': "E-mail du parent (facultatif)",
    'auth.create_btn': 'Créer le compte',
    'auth.creating': 'Création…',
    'auth.already': 'Déjà un compte ?',

    'grade.adult': 'Adulte',
    'grade.prefix': 'Classe',

    'header.dashboard_child': "Tableau de l'enfant",
    'header.dashboard_parent': 'Tableau du parent',
    'header.dashboard_teacher': "Tableau de l'enseignant",
    'header.language': 'Langue',

    'level.explorer': 'Explorateur',
    'level.adventurer': 'Aventurier',
    'level.scholar': 'Érudit',
    'level.sage': 'Sage',
    'level.luminary': 'Luminaire',
    'level.master': 'Maître',
    'level.knowble': 'Knowble',
    'level.pts': 'pts',
    'level.next_hint': 'points avant le niveau suivant',
    'level.max_hint': 'Tu as atteint le rang le plus élevé ! ⭐',

    'quiz.section_kicker': 'Quiz du jour',
    'quiz.headline': 'Des questions. De grandes récompenses.',
    'quiz.subline': "Réponds à autant que tu veux — chaque réponse te rapproche de Knowble ⭐",
    'quiz.start': 'Commencer →',
    'quiz.keep_going': 'Continuer !',
    'quiz.great_job': 'Bravo ! Prêt·e pour plus ?',
    'quiz.session_answered': "Tu as répondu à {n} question(s) cette session.",
    'quiz.session_hint': "Chaque réponse te rapproche de Knowble ⭐",
    'quiz.next': 'Question suivante →',
    'quiz.submit': 'Valider',
    'quiz.checking': 'Vérification…',
    'quiz.loading_more': 'Chargement…',
    'quiz.finished_btn': 'Retour au tableau',
    'quiz.correct': "Bravo, c'est juste !",
    'quiz.wrong_prefix': 'La bonne réponse est',
    'quiz.answered': 'Répondues',
    'quiz.pts_earned': 'points gagnés',
    'quiz.total_pts': 'Total',
    'quiz.no_questions': 'Aucune question disponible.',

    'subject.math': 'Mathématiques',
    'subject.science': 'Sciences',
    'subject.geography': 'Géographie',
    'subject.history': 'Histoire',
    'subject.language': 'Langue',
    'subject.social_studies': 'Éducation civique',
    'subject.nature': 'Nature',
    'subject.culture': 'Culture',
    'subject.general': 'Général',

    'habits.today': 'Mes habitudes',
    'habits.subtitle': 'De petites victoires, chaque jour.',
    'habits.nice_job': 'Bien joué !',
    'habits.error': "Impossible d'enregistrer",

    'parent.welcome': 'Bonjour',
    'parent.subtitle': "Suis l'apprentissage de ta famille et célèbre les victoires.",
    'parent.subscription_kicker': 'Formule Famille',
    'parent.plan_price': '/ mois',
    'parent.plan_desc': 'Accès illimité pour toute la famille, contenu étendu et rapports de progression.',
    'parent.active': 'Actif ✓',
    'parent.subscribe': "S'abonner",
    'parent.subscribe_loading': 'Chargement…',
    'parent.your_children': 'Vos enfants',
    'parent.no_children': 'Aucun enfant lié pour le moment.',
    'parent.link_hint': 'Demandez-leur de s\'inscrire avec votre e-mail pour lier les comptes.',
    'parent.wins': 'Réussites de la semaine',
    'parent.badge_streak_kicker': "Parent d'un Apprenant",
    'parent.badges_earned': 'badges gagnés',
    'parent.badge_hint': 'Un badge chaque semaine où votre enfant atteint au moins Bronze (5+ questions).',
    'parent.warming_up': 'En route',
    'parent.progress_to': 'Vers',
    'parent.share': 'Partager',
    'parent.week_headline': 'a eu une semaine {tier} !',
    'parent.tier_bronze': 'bronze',
    'parent.tier_silver': 'argent',
    'parent.tier_gold': 'or',
    'parent.tier_perfect': 'parfaite',
    'parent.tier_default': 'formidable',
    'parent.qh_summary': 'Niveau {level} · {q} question(s) · {h} habitude(s) complétée(s)',

    'teacher.kicker': 'Classe',
    'teacher.welcome': 'Bienvenue,',
    'teacher.subtitle': 'Suivez la progression quotidienne de vos élèves.',
    'teacher.students': 'Élèves',
    'teacher.avg_points': 'Points moy.',
    'teacher.total_pts': 'Points totaux',
    'teacher.progress': 'Progression des élèves',
    'teacher.no_students': 'Aucun élève pour le moment.',
  },

  de: {
    'brand.tagline1': '3 Fragen pro Tag.',
    'brand.tagline2': 'Ein Leben voller Neugier.',
    'landing.hero.desc': 'Begleitet Ouistiti, Stupidopoulos, Stupidopoulou und Patapouf auf einem täglichen Abenteuer durch Wissenschaft, Geographie, Geschichte, Sprache und mehr — gemacht für Kinder, Eltern und Lehrer.',
    'landing.family_learns': 'Eine Familie, die zusammen lernt',
    'landing.demo_note': 'Demo-Konten: parent@test.com · child@test.com · teacher@test.com · Passwort: test123',
    'feature.daily_title': 'Kurzes Tages-Quiz',
    'feature.daily_desc': 'Lerne jeden Tag ein wenig mit sofortigem Feedback und Level-Ups.',
    'feature.levels_title': '7 Ränge',
    'feature.levels_desc': 'Vom Entdecker bis Knowble ⭐ — jede Antwort bringt dich weiter.',
    'feature.roles_title': 'Drei Rollen, eine App',
    'feature.roles_desc': 'Kinder spielen, Eltern begleiten, Lehrer coachen — alles an einem Ort.',
    'footer.tag': 'Mutig lernen, gemeinsam.',

    'cta.start_free': 'Kostenlos loslegen',
    'cta.have_account': 'Ich habe ein Konto',
    'cta.log_in': 'Anmelden',
    'cta.join_free': 'Kostenlos beitreten',
    'cta.logout': 'Abmelden',
    'cta.back_home': 'Zurück zur Übersicht',

    'auth.welcome_back': 'Willkommen zurück',
    'auth.login_desc': 'Melde dich an, um weiterzulernen.',
    'auth.email': 'E-Mail',
    'auth.password': 'Passwort',
    'auth.login_btn': 'Anmelden',
    'auth.signing_in': 'Anmeldung…',
    'auth.quick_demo': 'Schneller Demo-Zugang',
    'auth.new_here': 'Neu hier?',
    'auth.create_account': 'Konto erstellen',
    'auth.register_title': 'Konto erstellen',
    'auth.register_desc': 'Wähle deine Rolle, um loszulegen.',
    'auth.role_child': 'Kind',
    'auth.role_parent': 'Elternteil',
    'auth.role_teacher': 'Lehrer',
    'auth.role_child_desc': 'Spielen, lernen & aufsteigen',
    'auth.role_parent_desc': 'Verfolgen & abonnieren',
    'auth.role_teacher_desc': 'Die Klasse begleiten',
    'auth.pick_character': 'Wähle deine Figur',
    'auth.name': 'Vollständiger Name',
    'auth.country': 'Land',
    'auth.grade': 'Klassenstufe',
    'auth.language': 'Sprache',
    'auth.parent_email': 'E-Mail der Eltern (optional)',
    'auth.create_btn': 'Konto erstellen',
    'auth.creating': 'Erstellen…',
    'auth.already': 'Schon ein Konto?',

    'grade.adult': 'Erwachsener',
    'grade.prefix': 'Klasse',

    'header.dashboard_child': 'Kinder-Übersicht',
    'header.dashboard_parent': 'Eltern-Übersicht',
    'header.dashboard_teacher': 'Lehrer-Übersicht',
    'header.language': 'Sprache',

    'level.explorer': 'Entdecker',
    'level.adventurer': 'Abenteurer',
    'level.scholar': 'Gelehrter',
    'level.sage': 'Weise',
    'level.luminary': 'Leuchte',
    'level.master': 'Meister',
    'level.knowble': 'Knowble',
    'level.pts': 'Pkt',
    'level.next_hint': 'Punkte bis zum nächsten Level',
    'level.max_hint': 'Höchste Stufe erreicht! ⭐',

    'quiz.section_kicker': 'Heutiges Quiz',
    'quiz.headline': 'Fragen. Große Belohnungen.',
    'quiz.subline': 'Beantworte so viele du willst — jede Antwort bringt dich näher an Knowble ⭐',
    'quiz.start': 'Quiz starten →',
    'quiz.keep_going': 'Weiter!',
    'quiz.great_job': 'Super gemacht! Bereit für mehr?',
    'quiz.session_answered': 'Du hast in dieser Sitzung {n} Frage(n) beantwortet.',
    'quiz.session_hint': 'Jede Antwort bringt dich näher an Knowble ⭐',
    'quiz.next': 'Nächste Frage →',
    'quiz.submit': 'Antwort abgeben',
    'quiz.checking': 'Wird geprüft…',
    'quiz.loading_more': 'Lädt…',
    'quiz.finished_btn': 'Zurück zur Übersicht',
    'quiz.correct': 'Genau richtig!',
    'quiz.wrong_prefix': 'Die richtige Antwort ist',
    'quiz.answered': 'Beantwortet',
    'quiz.pts_earned': 'Punkte',
    'quiz.total_pts': 'Gesamt',
    'quiz.no_questions': 'Keine Fragen verfügbar.',

    'subject.math': 'Mathematik',
    'subject.science': 'Naturwissenschaft',
    'subject.geography': 'Geographie',
    'subject.history': 'Geschichte',
    'subject.language': 'Sprache',
    'subject.social_studies': 'Sachkunde',
    'subject.nature': 'Natur',
    'subject.culture': 'Kultur',
    'subject.general': 'Allgemein',

    'habits.today': 'Meine Gewohnheiten',
    'habits.subtitle': 'Kleine Erfolge, jeden Tag.',
    'habits.nice_job': 'Sehr gut!',
    'habits.error': 'Konnte nicht speichern',

    'parent.welcome': 'Hallo',
    'parent.subtitle': 'Verfolge das Lernen deiner Familie und feiere die Erfolge.',
    'parent.subscription_kicker': 'Familien-Plan',
    'parent.plan_price': '/ Monat',
    'parent.plan_desc': 'Unbegrenzter Zugang für die ganze Familie, erweiterte Inhalte und Berichte.',
    'parent.active': 'Aktiv ✓',
    'parent.subscribe': 'Abonnieren',
    'parent.subscribe_loading': 'Lädt…',
    'parent.your_children': 'Deine Kinder',
    'parent.no_children': 'Noch keine Kinder verknüpft.',
    'parent.link_hint': 'Bitte sie, sich mit deiner E-Mail zu registrieren.',
    'parent.wins': 'Erfolge der Woche',
    'parent.badge_streak_kicker': 'Eltern eines Lernenden',
    'parent.badges_earned': 'Abzeichen erhalten',
    'parent.badge_hint': 'Ein Abzeichen für jede Woche, in der dein Kind mindestens Bronze erreicht (5+ Fragen).',
    'parent.warming_up': 'Aufwärmen',
    'parent.progress_to': 'Fortschritt zu',
    'parent.share': 'Teilen',
    'parent.week_headline': 'hatte eine {tier} Woche!',
    'parent.tier_bronze': 'Bronze',
    'parent.tier_silver': 'Silber',
    'parent.tier_gold': 'Gold',
    'parent.tier_perfect': 'perfekte',
    'parent.tier_default': 'starke',
    'parent.qh_summary': '{level} Level · {q} Frage(n) beantwortet · {h} Gewohnheit(en) erledigt',

    'teacher.kicker': 'Klasse',
    'teacher.welcome': 'Willkommen,',
    'teacher.subtitle': 'Verfolge den täglichen Fortschritt deiner Schüler.',
    'teacher.students': 'Schüler',
    'teacher.avg_points': 'Ø Punkte',
    'teacher.total_pts': 'Gesamt-Pkt',
    'teacher.progress': 'Schülerfortschritt',
    'teacher.no_students': 'Noch keine Schüler.',
  },
};

const safeLang = (l) => (['en', 'fr', 'de'].includes(l) ? l : 'en');

export const rawT = (key, lang = 'en', vars = null) => {
  const L = safeLang(lang);
  let s = (T[L] && T[L][key]) || T.en[key] || key;
  if (vars) {
    Object.entries(vars).forEach(([k, v]) => {
      s = s.replaceAll(`{${k}}`, String(v));
    });
  }
  return s;
};

export const useT = () => {
  const { user } = useAuth() || {};
  const stored = typeof window !== 'undefined' ? localStorage.getItem('ouistupastu_lang') : null;
  const lang = safeLang(user?.language || stored || 'en');
  return {
    lang,
    t: (key, vars) => rawT(key, lang, vars),
  };
};

export const setStoredLang = (lang) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('ouistupastu_lang', safeLang(lang));
  }
};

export const COUNTRIES = [
  { code: 'DE', name_en: 'Germany',       name_fr: 'Allemagne',   name_de: 'Deutschland' },
  { code: 'FR', name_en: 'France',        name_fr: 'France',       name_de: 'Frankreich' },
  { code: 'US', name_en: 'United States', name_fr: 'États-Unis',   name_de: 'USA' },
  { code: 'UK', name_en: 'United Kingdom',name_fr: 'Royaume-Uni',  name_de: 'Vereinigtes Königreich' },
  { code: 'CH', name_en: 'Switzerland',   name_fr: 'Suisse',       name_de: 'Schweiz' },
  { code: 'BE', name_en: 'Belgium',       name_fr: 'Belgique',     name_de: 'Belgien' },
  { code: 'CA', name_en: 'Canada',        name_fr: 'Canada',       name_de: 'Kanada' },
  { code: 'INT',name_en: 'International', name_fr: 'International',name_de: 'International' },
  { code: 'Other', name_en: 'Other',      name_fr: 'Autre',        name_de: 'Andere' },
];

export const countryLabel = (code, lang) => {
  const c = COUNTRIES.find((x) => x.code === code) || COUNTRIES[7];
  return c[`name_${safeLang(lang)}`];
};

export const defaultLangForCountry = (code) => {
  if (['DE', 'AT', 'CH'].includes(code)) return 'de';
  if (['FR', 'BE', 'LU'].includes(code)) return 'fr';
  return 'en';
};
